import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Avatar,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { NavContext } from "./ServiceProvDrawer";
import CategoryIcon from "@mui/icons-material/Category"; // Raw Material Request
import LocalDiningIcon from "@mui/icons-material/LocalDining";

// --- Enhanced Styled Small Dashboard Card ---
const SmallCard = styled(motion(Card))(({ theme, index }) => ({
  width: 130,
  height: 140,
  borderRadius: theme.spacing(3),
  background: [
    "linear-gradient(135deg, #EF4444, #FF8787)", // Red to Coral
    "linear-gradient(135deg, #9333EA, #F472B6)", // Purple to Pink
    "linear-gradient(135deg, #F97316, #FDE047)", // Orange to Yellow
    "linear-gradient(135deg, #16A34A, #86EFAC)", // Green to Lime
  ][index % 4],
  boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.4s ease",
  border: "2px solid rgba(255,255,255,0.2)",
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(180deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))",
    opacity: 0.9,
    borderRadius: theme.spacing(3),
  },
  "&:hover": {
    boxShadow: "0 12px 25px rgba(0,0,0,0.15)",
    transform: "translateY(-5px)",
  },
}));

const IconCircle = styled(Avatar)(({ theme, index }) => ({
  width: 50,
  height: 50,
  background: "#FFFFFF",
  color: [
    "#EF4444", // Blue
    "#3B82F6", // Green
    "#F59E0B", // Yellow
    "#10B981", // Red
  ][index % 4],
  marginBottom: theme.spacing(2),
  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
  transition: "transform 0.3s ease",
  border: `3px solid ${theme.palette.background.paper}`,
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

const DashboardCard = ({ icon, label, onClick, index }) => {
  const { setNavValue } = useContext(NavContext);
  return (
    <SmallCard
      whileHover={{ scale: 1.1, rotate: 3 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => {
        onClick();
        if (typeof setNavValue === "function") {
          setNavValue(index);
        }
      }}
      index={index}
    >
      <IconCircle index={index}>{icon}</IconCircle>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          textAlign: "center",
          fontSize: "1rem",
          color: "#FFFFFF",
          textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
        }}
      >
        {label}
      </Typography>
    </SmallCard>
  );
};

const ServiceProvPortal = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { setNavValue } = useContext(NavContext);

  const cards = [
    {
      label: "Dashboard",
      route: "/servicepro-dashboard",
      icon: <DashboardIcon fontSize="small" />,
    },
    {
      label: "Profile",
      route: "/servicepro-profile",
      icon: <PersonIcon fontSize="small" />,
    },
    {
      label: "Accept Tasks",
      route: "/tasks/accept",
      icon: <AssignmentTurnedInIcon fontSize="small" />,
    },
    {
      label: "Task Status",
      route: "/tasks/status",
      icon: <AssessmentIcon fontSize="small" />,
    },
  //   {
  //   label: "RM Request",
  //   route: "/raw-material-request",
  //   icon: <CategoryIcon fontSize="small" />,
  // },
  // {
  //   label: "RM Consumption",
  //   route: "/rm-consumption",
  //   icon: <LocalDiningIcon fontSize="small" />,
  // },
  ];

  // Update navValue based on current route
  useEffect(() => {
    const currentIndex = cards.findIndex((card) => card.route === location.pathname);
    if (currentIndex !== -1 && typeof setNavValue === "function") {
      setNavValue(currentIndex);
    }
  }, [location.pathname, setNavValue, cards]);

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#F3F4F6",
        overflow: "hidden",
        display: { xs: "flex", sm: "none", md: 'none' },
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundImage: "url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h5"
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.6rem", sm: "2rem" },
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 3,
            letterSpacing: "0.5px",
          }}
        >
          Service Portal
        </Typography>

        {/* Grid of Cards */}
        <Grid container spacing={3} justifyContent="center">
          {cards.map((card, idx) => (
            <Grid item key={idx}>
              <DashboardCard
                icon={card.icon}
                label={card.label}
                index={idx}
                onClick={() => navigate(card.route)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ServiceProvPortal;